import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {CampaignMemberDto} from "../../model/dto/entity/campaign-member.dto";
import {CampaignMemberAssignmentHistoryDto} from "../../model/dto/entity/campaign-member-assignment-history.dto";

export interface AssignMemberRequest {
    agentId: number;
    reason?: string;
}

export interface BulkAssignRequest {
    memberIds: number[];
    agentId: number;
    reason?: string;
}

export interface ReassignMembersRequest {
    fromAgentId: number;
    toAgentId: number;
    reason?: string;
}

export interface BulkAssignResponse {
    assignedCount: number;
    requestedCount: number;
    members: CampaignMemberDto[];
}

export interface ReassignResponse {
    reassignedCount: number;
    fromAgentId: number;
    toAgentId: number;
}

export interface AutoAssignResponse {
    assignedCount: number;
    campaignId: number;
}

export interface AssignmentStatistics {
    total: number;
    assigned: number;
    unassigned: number;
    byAgent: { [agentId: number]: number };
    byMethod: { [method: string]: number };
}

@Injectable({
    providedIn: 'root'
})
export class CampaignMemberAssignmentService {

    private readonly baseUrl = MvsCrudService.baseUrl + '/ct/campaignMemberAssignments';

    constructor(protected http: HttpClient) {
    }

    /**
     * Assigns a single member to an agent.
     */
    assignMember(memberId: number, request: AssignMemberRequest): Observable<CampaignMemberDto> {
        return this.http.post<CampaignMemberDto>(`${this.baseUrl}/members/${memberId}/assign`, request);
    }

    /**
     * Unassigns a member (removes agent assignment).
     */
    unassignMember(memberId: number, reason?: string): Observable<CampaignMemberDto> {
        const params = reason ? {reason} : {};
        return this.http.post<CampaignMemberDto>(`${this.baseUrl}/members/${memberId}/unassign`, null, {params});
    }

    /**
     * Bulk assigns multiple members to an agent.
     */
    bulkAssign(request: BulkAssignRequest): Observable<BulkAssignResponse> {
        return this.http.post<BulkAssignResponse>(`${this.baseUrl}/bulk-assign`, request);
    }

    /**
     * Reassigns all members from one agent to another for a campaign.
     */
    reassignMembers(campaignId: number, request: ReassignMembersRequest): Observable<ReassignResponse> {
        return this.http.post<ReassignResponse>(`${this.baseUrl}/campaigns/${campaignId}/reassign`, request);
    }

    /**
     * Executes automatic assignment for unassigned members in a campaign.
     */
    executeAutoAssignment(campaignId: number): Observable<AutoAssignResponse> {
        return this.http.post<AutoAssignResponse>(`${this.baseUrl}/campaigns/${campaignId}/auto-assign`, {});
    }

    /**
     * Gets the assignment history for a campaign member.
     */
    getAssignmentHistory(memberId: number): Observable<CampaignMemberAssignmentHistoryDto[]> {
        return this.http.get<CampaignMemberAssignmentHistoryDto[]>(`${this.baseUrl}/members/${memberId}/history`);
    }

    /**
     * Gets all unassigned members for a campaign.
     */
    getUnassignedMembers(campaignId: number): Observable<CampaignMemberDto[]> {
        return this.http.get<CampaignMemberDto[]>(`${this.baseUrl}/campaigns/${campaignId}/unassigned`);
    }

    /**
     * Gets members assigned to a specific agent in a campaign.
     */
    getMembersAssignedToAgent(campaignId: number, agentId: number): Observable<CampaignMemberDto[]> {
        return this.http.get<CampaignMemberDto[]>(`${this.baseUrl}/campaigns/${campaignId}/agents/${agentId}/members`);
    }

    /**
     * Gets assignment statistics for a campaign.
     */
    getAssignmentStatistics(campaignId: number): Observable<AssignmentStatistics> {
        return this.http.get<AssignmentStatistics>(`${this.baseUrl}/campaigns/${campaignId}/statistics`);
    }
}
